import { useEffect, useState, useRef } from "react";
import {
  Category,
  changeStatus,
  deleteCategory,
  fetchCategories,
  uploadCategory,
} from "../../app/services/CategoryService";
import { toast } from "react-toastify";
import PageWrapper from "../../app/shared/common/PageWrapper";
import CreateTable from "../../app/shared/common/table/CreateTable";
import TableActions from "../../app/shared/common/table/TableAction";
import { Button } from "@mui/material";
import axios from "axios";

const tableColumns = [
  { key: "code", label: "Code" },
  { key: "image", label: "Image" },
  { key: "name", label: "Name" },
  { key: "is_active", label: "Status" },
];

const CategoryPage = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleOpenDialog = (id: number) => {
    setSelectedCategoryId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCategoryId(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCategoryId) return;

    try {
      await deleteCategory(selectedCategoryId);
      setCategories((prev) =>
        prev.filter((cat) => cat.code !== selectedCategoryId)
      );
      toast.success("Category deleted successfully.");
    } catch (err) {
      toast.error("Something went wrong while deleting.");
    } finally {
      handleCloseDialog();
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await changeStatus(id);
      setCategories((prev) =>
        prev.map((p) => (p.code === id ? { ...p, is_active: !p.is_active } : p))
      );
      toast("Status changed successfully");
    } catch (error) {
      toast.error("Error updating category status");
    }
  };

  // ---- Upload Handling ----
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      // const res = await axios.post(
      //   "https://uharvest-non-prod-evfrc2d5f6hpa5dn.centralindia-01.azurewebsites.net/category/create-by-file",
      //   formData,
      //   {
      //     headers: { "Content-Type": "multipart/form-data" },
      //   }
      // );
      const res = await uploadCategory(formData);

      if (res.data.status === "success") {
        toast.success(res.data.message);

        // 🔄 Reload categories after successful upload
        const reload = await fetchCategories();
        if (reload.status === "success") {
          setCategories(reload.data);
        } else {
          toast.error(reload.message);
        }
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("File upload failed");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // reset input
      }
    }
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        const res = await fetchCategories();
        if (res.status === "success") {
          setCategories(res.data);
        } else {
          toast.error(res.message);
        }
      } catch (err) {
        toast.error("Failed to load categories.");
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  return (
    <PageWrapper
      title={"Categories"}
      navigateTo={"/catalog/category/add"}
      buttonTitle={"Add Category"}
      actionName={"category"}
      open={openDialog}
      onClose={handleCloseDialog}
      onConfirm={handleConfirmDelete}
      extraAction={
        <>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#fcb500",
              color: "#000",
              fontWeight: 600,
            }}
            onClick={handleUploadClick}
          >
            Upload Category
          </Button>
          {/* Hidden file input */}
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </>
      }
    >
      <CreateTable
        columns={tableColumns}
        data={categories}
        loading={loading}
        onCheckboxClick={(row) => handleToggleStatus(row.code)}
        renderActions={(cat) => (
          <TableActions
            navigateTo={`/catalog/category/${cat.code}`}
            onClick={() => handleOpenDialog(cat.code)}
          />
        )}
      />
    </PageWrapper>
  );
};

export default CategoryPage;
